import { useState } from "react";
import { Controller, useFieldArray, useForm, useWatch, type Control } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  ConfirmModal,
  CopyableId,
  Input,
  Modal,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
  Textarea,
} from "@fox1ck-org/ui";
import { Form } from "@fox1ck-org/ui/form";
import { toast } from "@fox1ck-org/ui/toast";
import type { EntryPoint } from "@gen/tracker/v1/entry_point_pb";
import type { DomainCampaign, TrackingDomain } from "@gen/tracker/v1/tracking_domain_pb";

import { WriteOnly } from "@/components/auth/write-only";
import { RhfField } from "@/components/rhf-field";
import { useT, type TKey } from "@/lib/i18n";
import { MATCH_FIELDS, MATCH_OPS, isPresenceOp, matchFieldLabel, matchOpLabel, matchValuesHint } from "@/lib/match-fields";
import { useCanWrite } from "@/lib/permissions";
import { useDomainCampaigns } from "@/lib/services/tracking-domain-service";
import { useArchiveEntryPoint, useBindCampaign, useListEntryPoints, useSaveEntryPoint, useUnbindCampaign } from "@/lib/services/entry-point-service";
import {
  ENTRY_FALLBACKS,
  ENTRY_RULES_MAX,
  ENTRY_STICKY_CHOICES,
  ENTRY_TOKEN_RE,
  NEW_ENTRY_POINT,
  entryPointFormSchema,
  entryPointToForm,
  formToEntryPointInput,
  type EntryPointFormValues,
} from "@/lib/schemas/entry-point";

const FALLBACK_LABEL: Record<(typeof ENTRY_FALLBACKS)[number], TKey> = {
  origin: "entry.fallback.origin",
  not_found: "entry.fallback.not_found",
  redirect: "entry.fallback.redirect",
  campaign: "entry.fallback.campaign",
};
const STICKY_LABEL: Record<number, TKey> = {
  0: "entry.sticky.off",
  3600: "entry.sticky.hour",
  86400: "entry.sticky.day",
  604800: "entry.sticky.week",
  2592000: "entry.sticky.month",
};

/** A Connect failure as the screen says it. */
function failure(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? `${fallback}: ${err.message}` : fallback;
}

/**
 * Входы домена: the paths of the domain that hand each visit to one of
 * several campaigns — by a token in the one ad link (`?<token>`), a rule, the
 * visitor's cookie, else a fallback (docs/specs/use-cases/entry-point.md).
 */
export function DomainEntryPoints({ domain }: { domain: TrackingDomain }) {
  const t = useT();
  const entries = useListEntryPoints(domain.id);
  const [open, setOpen] = useState<EntryPoint | "new" | null>(null);
  const items = entries.data ?? [];
  const current = open && open !== "new" ? (items.find((e) => e.id === open.id) ?? open) : undefined;

  return (
    <section className="flex flex-col gap-2" aria-label={t("entry.title")}>
      <div className="flex items-center gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("entry.title")}</h3>
        <WriteOnly>
          <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setOpen("new")}>
            <Plus className="size-3.5" /> {t("entry.add")}
          </Button>
        </WriteOnly>
      </div>
      {entries.isPending ? (
        <Skeleton className="h-14 w-full" />
      ) : items.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t("entry.none")}</p>
      ) : (
        <div className="overflow-hidden rounded-md border border-border">
          {items.map((e) => (
            <div key={e.id} className="flex items-center gap-3 border-b border-border px-3 py-2.5 last:border-b-0">
              <Badge variant={e.status === "PAUSED" ? "warn" : "ok"} dot>
                {t(e.status === "PAUSED" ? "lib.status.paused" : "lib.status.active")}
              </Badge>
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm">{e.name}</span>
                <span className="truncate font-mono text-xs text-muted-foreground">
                  {domain.fqdn}
                  {e.path === "/" ? "/" : e.path} · {t("entry.campaigns.count", { n: e.campaigns.length })}
                </span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setOpen(e)}>
                {t("ops.open")}
              </Button>
            </div>
          ))}
        </div>
      )}
      {open ? <EntryPointModal domain={domain} entry={current} onClose={() => setOpen(null)} /> : null}
    </section>
  );
}

function EntryPointModal({ domain, entry, onClose }: { domain: TrackingDomain; entry: EntryPoint | undefined; onClose: () => void }) {
  const t = useT();
  const canWrite = useCanWrite();
  const save = useSaveEntryPoint();
  const archive = useArchiveEntryPoint();
  const campaigns = useDomainCampaigns(domain.id).data ?? [];
  const [archiving, setArchiving] = useState(false);
  const form = useForm<EntryPointFormValues>({
    resolver: zodResolver(entryPointFormSchema) as never,
    defaultValues: entry ? entryPointToForm(entry) : NEW_ENTRY_POINT,
  });
  const c = form.control;
  const rules = useFieldArray({ control: c, name: "rules" });
  const fallback = useWatch({ control: c, name: "fallback" });

  const onSubmit = form.handleSubmit((v) =>
    save.mutate(
      { id: entry?.id, trackingDomainId: domain.id, input: formToEntryPointInput(v) },
      {
        onSuccess: () => {
          toast.success(t(entry ? "entry.saved" : "entry.created"));
          if (!entry) onClose();
        },
        onError: (e) => toast.error(failure(e, t("entry.saveFailed"))),
      },
    ),
  );

  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="lg"
        title={entry ? entry.name : t("entry.new")}
        footer={
          <div className="flex w-full items-center gap-2">
            {entry && canWrite ? (
              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setArchiving(true)}>
                {t("flows.archive")}
              </Button>
            ) : null}
            <Button variant="secondary" className="ml-auto" onClick={onClose}>
              {t("rule.ed.cancel")}
            </Button>
            {canWrite ? (
              <Button variant="primary" onClick={onSubmit} disabled={save.isPending}>
                {entry ? t("ws.save") : t("entry.create")}
              </Button>
            ) : null}
          </div>
        }
      >
        <Form {...form}>
          <form className="flex flex-col gap-4" onSubmit={onSubmit}>
            <div className="grid grid-cols-[minmax(0,1fr)_--spacing(40)] gap-3">
              <RhfField control={c} name="name" label={t("flows.col.name")}>
                {(field) => <Input {...field} font="sans" placeholder={t("entry.name.ph")} />}
              </RhfField>
              <RhfField control={c} name="path" label={t("entry.path")} description={domain.fqdn}>
                {(field) => <Input {...field} className="font-mono" placeholder="/" />}
              </RhfField>
            </div>

            <fieldset className="flex flex-col gap-3 border-t border-border pt-3">
              <legend className="pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("entry.selectors")}</legend>
              <Controller
                control={c}
                name="byKey"
                render={({ field, fieldState }) => (
                  <label className="flex items-center justify-between gap-3 text-sm">
                    <span>
                      {t("entry.byKey")}
                      <span className="block font-mono text-xs text-muted-foreground">https://{domain.fqdn}/?6f1c…</span>
                      {fieldState.error ? <span className="block text-xs text-destructive">{fieldState.error.message}</span> : null}
                    </span>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </label>
                )}
              />
              <RhfField control={c} name="byParam" label={t("entry.byParam")} optional description={t("entry.byParam.hint")}>
                {(field) => <Input {...field} className="font-mono" placeholder="c" />}
              </RhfField>
            </fieldset>

            <fieldset className="flex flex-col gap-2 border-t border-border pt-3">
              <legend className="pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("entry.rules")}</legend>
              <p className="text-xs text-muted-foreground">{t("entry.rules.hint")}</p>
              {rules.fields.map((r, i) => (
                <RuleRow key={r.id} control={c} index={i} campaigns={campaigns} onRemove={() => rules.remove(i)} />
              ))}
              {rules.fields.length < ENTRY_RULES_MAX ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="self-start"
                  onClick={() => rules.append({ field: "country", param: "", op: "in", values: "", campaignId: "" })}
                >
                  <Plus className="size-3.5" /> {t("entry.rules.add")}
                </Button>
              ) : null}
            </fieldset>

            <fieldset className="grid grid-cols-2 gap-3 border-t border-border pt-3">
              <RhfField control={c} name="fallback" label={t("entry.fallback")}>
                {(field) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ENTRY_FALLBACKS.map((k) => (
                        <SelectItem key={k} value={k}>
                          {t(FALLBACK_LABEL[k])}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </RhfField>
              <RhfField control={c} name="stickySeconds" label={t("entry.sticky")}>
                {(field) => (
                  <Select value={String(field.value)} onValueChange={(v) => field.onChange(Number(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ENTRY_STICKY_CHOICES.map((s) => (
                        <SelectItem key={s} value={String(s)}>
                          {t(STICKY_LABEL[s]!)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </RhfField>
              {fallback === "redirect" ? (
                <div className="col-span-2">
                  <RhfField control={c} name="fallbackUrl" label={t("entry.fallback.url")}>
                    {(field) => <Input {...field} className="font-mono" placeholder="https://" />}
                  </RhfField>
                </div>
              ) : null}
              {fallback === "campaign" ? (
                <div className="col-span-2">
                  <RhfField control={c} name="fallbackCampaignId" label={t("entry.fallback.campaign.label")}>
                    {(field) => <CampaignSelect value={field.value} onChange={field.onChange} campaigns={campaigns} />}
                  </RhfField>
                </div>
              ) : null}
            </fieldset>

            <Controller
              control={c}
              name="active"
              render={({ field }) => (
                <label className="flex items-center justify-between gap-3 border-t border-border pt-3 text-sm">
                  <span>
                    {t("entry.active")}
                    <span className="block text-xs text-muted-foreground">{t("entry.active.hint")}</span>
                  </span>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </label>
              )}
            />
          </form>
        </Form>
        {entry ? <EntryCampaigns entry={entry} campaigns={campaigns} /> : null}
      </Modal>
      <ConfirmModal
        open={archiving}
        onClose={() => setArchiving(false)}
        tone="danger"
        loading={archive.isPending}
        title={t("entry.archive.title", { name: entry?.name ?? "" })}
        description={t("entry.archive.body")}
        confirmLabel={t("flows.archive")}
        onConfirm={() => {
          if (!entry) return;
          archive.mutate(entry.id, {
            onSuccess: () => {
              setArchiving(false);
              onClose();
            },
            onError: (e) => toast.error(failure(e, t("entry.saveFailed"))),
          });
        }}
      />
    </>
  );
}

function RuleRow({ control, index, campaigns, onRemove }: { control: Control<EntryPointFormValues>; index: number; campaigns: DomainCampaign[]; onRemove: () => void }) {
  const t = useT();
  const field = useWatch({ control, name: `rules.${index}.field` });
  const op = useWatch({ control, name: `rules.${index}.op` });
  return (
    <div className="grid grid-cols-[--spacing(32)_--spacing(28)_minmax(0,1fr)_--spacing(44)_auto] items-start gap-2 rounded-md border border-border p-2">
      <div className="flex flex-col gap-1">
        <RhfField control={control} name={`rules.${index}.field`} label={t("entry.rule.field")}>
          {(f) => (
            <Select value={f.value} onValueChange={f.onChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MATCH_FIELDS.map((k) => (
                  <SelectItem key={k} value={k}>
                    {matchFieldLabel(k)}
                  </SelectItem>
                ))}
                <SelectItem value="param">{t("match.field.param")}</SelectItem>
              </SelectContent>
            </Select>
          )}
        </RhfField>
        {field === "param" ? (
          <RhfField control={control} name={`rules.${index}.param`} label={t("entry.rule.param")}>
            {(f) => <Input {...f} className="font-mono" placeholder="utm_source" />}
          </RhfField>
        ) : null}
      </div>
      <RhfField control={control} name={`rules.${index}.op`} label={t("entry.rule.op")}>
        {(f) => (
          <Select value={f.value} onValueChange={f.onChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MATCH_OPS.map((k) => (
                <SelectItem key={k} value={k}>
                  {matchOpLabel(k)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </RhfField>
      {isPresenceOp(op) ? (
        <span />
      ) : (
        <RhfField control={control} name={`rules.${index}.values`} label={t("entry.rule.values")} description={matchValuesHint(field, op)}>
          {(f) => <Textarea {...f} rows={1} className="font-mono text-xs" />}
        </RhfField>
      )}
      <RhfField control={control} name={`rules.${index}.campaignId`} label={t("entry.rule.campaign")}>
        {(f) => <CampaignSelect value={f.value} onChange={f.onChange} campaigns={campaigns} />}
      </RhfField>
      <Button variant="ghost" size="sm" className="mt-6" aria-label={t("rule.ed.delete")} onClick={onRemove}>
        <Trash2 className="size-3.5" />
      </Button>
    </div>
  );
}

function CampaignSelect({ value, onChange, campaigns }: { value: string; onChange: (v: string) => void; campaigns: DomainCampaign[] }) {
  const t = useT();
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger>
        <SelectValue placeholder={t("entry.campaign.pick")} />
      </SelectTrigger>
      <SelectContent>
        {campaigns.map((c) => (
          <SelectItem key={c.id} value={c.id}>
            {c.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * The campaigns behind the entry point, each with its token and the ad link
 * it makes. Binding and unbinding act at once — the domain republishes.
 */
function EntryCampaigns({ entry, campaigns }: { entry: EntryPoint; campaigns: DomainCampaign[] }) {
  const t = useT();
  const bind = useBindCampaign();
  const unbind = useUnbindCampaign();
  const [pick, setPick] = useState("");
  const [token, setToken] = useState("");
  const bound = new Set(entry.campaigns.map((b) => b.campaignId));
  const free = campaigns.filter((c) => !bound.has(c.id));
  const tokenBad = token.trim() !== "" && !ENTRY_TOKEN_RE.test(token.trim());

  return (
    <section className="mt-4 flex flex-col gap-2 border-t border-border pt-3" aria-label={t("entry.campaigns")}>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("entry.campaigns")}</h3>
      {entry.campaigns.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t("entry.campaigns.none")}</p>
      ) : (
        <div className="overflow-hidden rounded-md border border-border">
          {entry.campaigns.map((b) => (
            <div key={b.campaignId} className="flex items-center gap-3 border-b border-border px-3 py-2 last:border-b-0">
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex items-center gap-2 truncate text-sm">
                  {b.campaignName}
                  {b.campaignStatus !== "ACTIVE" ? <Badge variant="warn">{t("entry.campaign.inactive")}</Badge> : null}
                </span>
                <CopyableId value={b.url} className="min-w-0 font-mono text-xs" />
              </div>
              <WriteOnly>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t("entry.unbind")}
                  disabled={unbind.isPending}
                  onClick={() =>
                    unbind.mutate({ entryPointId: entry.id, campaignId: b.campaignId }, { onError: (e) => toast.error(failure(e, t("entry.saveFailed"))) })
                  }
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </WriteOnly>
            </div>
          ))}
        </div>
      )}
      <WriteOnly>
        {free.length > 0 ? (
          <div className="grid grid-cols-[minmax(0,1fr)_--spacing(56)_auto] items-start gap-2">
            <CampaignSelect value={pick} onChange={setPick} campaigns={free} />
            <span className="flex flex-col gap-1">
              <Input value={token} onChange={(e) => setToken(e.target.value)} className="font-mono" placeholder={t("entry.token.ph")} aria-label={t("entry.token")} aria-invalid={tokenBad} />
              {tokenBad ? <span className="text-xs text-destructive">{t("entry.token.bad")}</span> : null}
            </span>
            <Button
              variant="secondary"
              disabled={!pick || tokenBad || bind.isPending}
              onClick={() =>
                bind.mutate(
                  { entryPointId: entry.id, campaignId: pick, token: token.trim() },
                  {
                    onSuccess: () => {
                      setPick("");
                      setToken("");
                    },
                    onError: (e) => toast.error(failure(e, t("entry.saveFailed"))),
                  },
                )
              }
            >
              {t("entry.bind")}
            </Button>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">{t("entry.campaigns.allBound")}</p>
        )}
      </WriteOnly>
    </section>
  );
}
